import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MessageService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { MultiSelectModule } from "primeng/multiselect";
import { ConfigScope, ModuleConfigTabsService } from "./module-config-tabs.service";

const DEFAULT_ASPECTS = [
  "Quality",
  "Productivity",
  "Efficiency",
  "Cost",
  "Reliability",
  "Compliance",
];

const ANALYSIS_MODES = [
  { id: "anomalies", label: "Anomalies" },
  { id: "trends", label: "Trends" },
];

/** Recognised in a formula, so they are not reported as unresolved parameter names. */
const FORMULA_FUNCTIONS = [
  "min", "max", "abs", "avg", "sum", "sqrt", "log", "ln", "exp", "pow",
  "round", "floor", "ceil", "mean", "median", "count", "if", "and", "or", "not",
];

/** Delimiter for substitution placeholders - a control char cannot occur in a formula. */
const PLACEHOLDER = "\u0001";

type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

interface SaveQueue {
  timer: any;
  state: SaveState;
  loading: boolean;
  dirty: boolean;
}

/**
 * The three config sets for one module, held in memory because the owning record does
 * not exist yet. Shape is the body of the three save endpoints minus their scope keys
 * (`subcategory_id` and friends) — the owner is not known while a draft is being made,
 * and the module is identified by the entry this draft is attached to.
 *
 * Travels in the sub-category create payload under `moduleConfig.moduleConfigs[key]`;
 * the backend writes it into the `subCategory_*` tables in the same transaction that
 * creates the sub-category. See CATEGORY_TO_SITE_CONFIG_COPY_SPEC.md §10.
 */
export interface ModuleConfigDraft {
  moduleTarget: any;
  moduleIndexes: any;
  behaviourFinetuning: any;
}

/**
 * Whether a module has any Module Target, Module Indexes or Behaviour & Finetuning
 * config. Takes the three read responses, or a draft — they share field names.
 *
 * An unselected KPI row does not count: every target save writes a row per
 * parameter, selected or not, so "has rows" would be true for a module nobody
 * configured.
 */
export function hasModuleConfig(config: Partial<ModuleConfigDraft> | null | undefined): boolean {
  const target = config?.moduleTarget;
  return !!(
    target?.module_aspects?.length ||
    (target?.kpis ?? []).some((kpi: any) => kpi?.is_selected && !kpi?.is_removed) ||
    config?.moduleIndexes?.indexes?.length ||
    (config?.behaviourFinetuning?.behaviours ?? []).some(
      (record: any) => String(record?.behaviour ?? "").trim() || String(record?.finetuning ?? "").trim()
    )
  );
}

/**
 * One other module configured on the same owner, as far as formula resolution cares.
 * `moduleCode` is the unit's grid code (channel + position, e.g. "1B") — the key the
 * config endpoints scope on, used here to read that unit's index names. It is not
 * written into a formula: a parameter code already carries its unit as a prefix.
 */
export interface SiblingModule {
  moduleCode: string;
  moduleId: string | number;
  /** Alias or module name, used only in messages. */
  label: string;
  parameters: { name: string, code: string }[];
  /**
   * That unit's indexes. May be empty until fetched — see `ensureSiblingIndexes`. A
   * `code` is blank on an index the backend has not assigned one to yet, which makes it
   * unusable as an operand; `pendingIndexNames` turns that into its own message.
   */
  indexes?: { name: string, code: string }[];
}

/** One resolvable operand name. `code` is the text substituted into the PureBI form. */
interface DictionaryEntry {
  name: string;
  code: string;
  /** null for this module's own parameters; the owning unit's label otherwise. */
  owner: string | null;
  kind: "parameter" | "index";
}

/**
 * Target / Indexes / Behaviour configuration for ONE module inside ONE owning entity —
 * either a site or a sub-category, selected by the `scope` input.
 *
 * Unlike the module-library equivalent this component is scoped to a single module at
 * a time (it lives inside the per-module config dialog), so it holds no per-module
 * caches — the dialog is destroyed and recreated when a different module is opened.
 */
@Component({
  selector: "app-module-config-tabs",
  standalone: true,
  imports: [CommonModule, FormsModule, ToastModule, MultiSelectModule],
  templateUrl: "./module-config-tabs.component.html",
  styleUrls: ["./module-config-tabs.component.css"],
})
export class ModuleConfigTabsComponent implements OnChanges, OnDestroy {
  /** Which panel to render. The tab strip itself lives in the host dialog. */
  @Input() tab: "parameters" | "target" | "indexes" | "behaviour" = "target";
  /** Whether this module is being configured under a site or a sub-category. */
  @Input() scope: ConfigScope = "subcategory";
  /** Site mode only: the site being edited (route param `siteid`). */
  @Input() siteId: string | number;
  /**
   * Site mode only: the grid code of the specific unit being configured — channel plus
   * position, e.g. `"1A"`, or `"00"` / `"0LAB"` / `"0CP"` for the special modules.
   *
   * `moduleId` identifies a module *type* from the library, not the unit on the plant —
   * a site runs the same type many times (NEPL has 8 "Aerobic Bio" units: 1A, 1C, 1I,
   * 2C, 2E, 2H, 3E, 3G), and every one of those cards carries the same `module_id`.
   * Without this, configuring one unit writes config for all of them.
   *
   * NOT the library's module code, which is shared by every unit of a type and would be
   * useless here. It is the grid cell the user clicked Configure on.
   *
   * Absent only for a unit with no grid position; the server then resolves the unit
   * itself when the module has one, and keeps module-wide behaviour when it has several.
   */
  @Input() moduleCode: string | number;
  /** Sub-category mode only: the record being edited (the `category_id` query param). */
  @Input() subCategoryId: string | number;
  /** Sub-category mode only: its parent (`category_master_id`, from Select Category). */
  @Input() categoryId: string | number;
  @Input() moduleId: string | number;
  /** Site or sub-category name, used in the "configured in library but not here" warning. */
  @Input() ownerName = "";
  /** Parameters of this module in this owner: { parameter_id, code, name, unit }. */
  @Input() parameters: any[] = [];
  /**
   * This module's Configure Parameter rows: { name, code }. Resolution only — it covers
   * the rows the Target grid drops (no `parameter_id` yet, or removed on the Target
   * tab), which the user can still see on tab 1 and will reasonably name in a formula.
   */
  @Input() configuredParameters: any[] = [];
  /**
   * Every OTHER module configured on this owner, so a formula can name a parameter or
   * an index that lives on a different unit. Supplied by the plant-customization screen,
   * which is the only place that holds all of them at once.
   */
  @Input() siblingModules: SiblingModule[] = [];

  /**
   * Draft mode — the owning record is being created and has no id yet, so there is
   * nothing to key a read or a write on. Set while a **new sub-category** is going
   * through the wizard.
   *
   * The tabs render and behave normally; only persistence differs. Nothing is read from
   * the scope endpoints (there is nothing there), the tab seeds from the module library
   * instead, and every edit is emitted on `draftChange` rather than POSTed. The dialog
   * hands the draft back to the wizard, which sends it with the create payload so the
   * sub-category and its config commit together.
   */
  @Input() draft = false;
  /**
   * A draft captured earlier for this same module, restored when the dialog is
   * reopened. Takes precedence over library seeding — reopening must show what the
   * user entered, not overwrite it with defaults.
   */
  @Input() draftValue: ModuleConfigDraft = null;

  /** Emits whenever a save lands, so the dialog can reflect unsaved state if it wants. */
  @Output() saveStateChange = new EventEmitter<SaveState>();
  /** Draft mode only: the three config sets, re-emitted on every edit. */
  @Output() draftChange = new EventEmitter<ModuleConfigDraft>();
  /**
   * Once per load, when all three reads have settled: whether the module already has
   * any config (see hasModuleConfig). The dialog uses it to open with the Module
   * Intelligence toggle on — which is why this component stays loaded while hidden.
   */
  @Output() configPresence = new EventEmitter<boolean>();

  @ViewChild("aspectDropdown") aspectDropdown: ElementRef<HTMLElement>;

  readonly analysisModes = ANALYSIS_MODES;
  aspectOptions: string[] = [...DEFAULT_ASPECTS];
  activeAspects: string[] = [];
  aspectDropdownOpen = false;
  newAspectName = "";

  targetRows: any[] = [];
  targetSearchTerm = "";

  /** Add Parameter picker - the KPI grid lists added rows only, this is where they come from. */
  addParamVisible = false;
  addParamSearchTerm = "";
  /** parameter_ids ticked in the picker, discarded unless Add is pressed. */
  private addParamChosen = new Set<string>();

  indexRows: any[] = [];
  /** True while Create Formula is fetching the other units' index names. */
  creatingFormula = false;

  behaviourAspect = "";
  behaviourRecord: any = null;

  confirmVisible = false;
  confirmTitle = "";
  confirmTarget = "";
  private confirmResolve: (value: boolean) => void = null;

  /** Full index records the System-Admin library defines for this module. */
  libraryIndexes: any[] = [];
  /** Their names, for the gap comparison and message. */
  libraryIndexNames: string[] = [];
  /** Outcome of the library cross-check, surfaced in the Indexes tab. */
  libraryCheckState: "pending" | "ok" | "unavailable" = "pending";
  libraryCheckDetail = "";

  private behaviourStore: { [aspect: string]: any } = {};
  private loadedFor = "";
  private indexLoadSettled = false;
  /** One fetch of the other units' indexes per dialog; see `ensureSiblingIndexes`. */
  private siblingIndexesLoaded = false;
  private saveQueues: { [kind: string]: SaveQueue } = {
    target: { timer: null, state: "idle", loading: false, dirty: false },
    index: { timer: null, state: "idle", loading: false, dirty: false },
    behaviour: { timer: null, state: "idle", loading: false, dirty: false },
  };

  constructor(
    private service: ModuleConfigTabsService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["parameters"]) {
      this.syncTargetRows();
    }
    if (
      changes["scope"] || changes["siteId"] || changes["moduleCode"] ||
      changes["subCategoryId"] || changes["moduleId"] || changes["draft"]
    ) {
      this.loadAll();
    }
  }

  ngOnDestroy(): void {
    // The dialog can close mid-debounce; don't lose the edit.
    Object.keys(this.saveQueues).forEach(kind => this.flushSave(kind));
  }

  /** The owning entity's id — site or sub-category, depending on scope. */
  private get ownerId(): string | number {
    return this.scope === "site" ? this.siteId : this.subCategoryId;
  }

  private get scopeKey(): string {
    // The unit is part of the key: two units of the same module type at the same site
    // share every other component of it, and they must not share a load.
    return `${this.scope}:${this.draft ? "draft" : this.ownerId ?? ""}`
      + `:${this.moduleId ?? ""}:${this.moduleCode ?? ""}`;
  }

  private get scopeRequest(): any {
    if (this.scope === "site") {
      return {
        site_id: this.siteId ?? null,
        module_id: this.moduleId ?? null,
      };
    }
    return {
      category_id: this.categoryId ?? null,
      subcategory_id: this.subCategoryId ?? null,
      module_id: this.moduleId ?? null,
    };
  }

  /**
   * Scope block for the six Target / Indexes / Behaviour endpoints. In site mode it also
   * carries `module_code`, the grid code of the unit, so a read or write lands on the
   * unit the user clicked rather than on every unit of that module type.
   *
   * Omitted rather than sent as null when there is no code — the server reads its
   * absence as "resolve the unit from site_id + module_id", which is the behaviour that
   * predates this field. Sub-category scope is unchanged: those endpoints do not take it.
   *
   * `deleteSiteAspect` deliberately keeps the plain `scopeRequest` — an aspect is global
   * vocabulary, not per-unit config.
   */
  private get moduleConfigRequest(): any {
    const request = this.scopeRequest;
    if (this.scope !== "site" || this.moduleCode == null || this.moduleCode === "") {
      return request;
    }
    return { ...request, module_code: this.moduleCode };
  }

  // ---------------------------------------------------------------------------
  // Load / save
  // ---------------------------------------------------------------------------

  saveState(kind: string): SaveState {
    return this.saveQueues[kind].state;
  }

  saveLabel(kind: string): string {
    switch (this.saveQueues[kind].state) {
      case "pending": return "Unsaved changes";
      case "saving": return "Saving…";
      // Nothing has been written yet while drafting, and saying so would be a lie the
      // user acts on - they would close the wizard believing the config is stored.
      case "saved": return this.draft ? "Saved with the sub-category" : "All changes saved";
      case "error": return "Save failed - retry";
      default: return "";
    }
  }

  queueSave(kind: string): void {
    const queue = this.saveQueues[kind];
    if (!this.moduleId || queue.loading) {
      return;
    }
    if (!this.draft && !this.ownerId) {
      return;
    }

    queue.dirty = true;
    queue.state = "pending";
    this.saveStateChange.emit("pending");
    clearTimeout(queue.timer);
    queue.timer = setTimeout(() => this.flushSave(kind), 800);
  }

  flushSave(kind: string): void {
    const queue = this.saveQueues[kind];
    clearTimeout(queue.timer);
    queue.timer = null;

    if (!queue.dirty) {
      return;
    }
    queue.dirty = false;

    if (this.draft) {
      // Nothing to POST to yet. The edit is handed to the wizard, which sends it with
      // the create payload - so it is "saved" only once the sub-category is.
      this.emitDraft();
      queue.state = "saved";
      this.saveStateChange.emit("saved");
      return;
    }

    queue.state = "saving";
    this.saveStateChange.emit("saving");

    this.saveRequest(kind).then((response: any) => {
      if (response && response.status === 1) {
        queue.state = "saved";
        this.saveStateChange.emit("saved");
        return;
      }
      this.failSave(kind, response?.msg);
    }).catch(() => this.failSave(kind));
  }

  private saveRequest(kind: string): Promise<any> {
    if (kind === "target") {
      return this.service.saveModuleTarget(this.scope, this.buildTargetRequest());
    }
    if (kind === "index") {
      return this.service.saveModuleIndexes(this.scope, this.buildIndexRequest());
    }
    return this.service.saveBehaviourFinetuning(this.scope, this.buildBehaviourRequest());
  }

  private failSave(kind: string, message?: string): void {
    const queue = this.saveQueues[kind];
    queue.dirty = true;
    queue.state = "error";
    this.saveStateChange.emit("error");
    this.messageService.add({
      severity: "error",
      summary: this.saveSummary(kind),
      detail: message || "Unable to save changes",
    });
  }

  private saveSummary(kind: string): string {
    if (kind === "target") return "Module Target";
    return kind === "index" ? "Module Indexes" : "Behaviour & Finetuning";
  }

  private loadAll(): void {
    const key = this.scopeKey;
    if (!this.moduleId || this.loadedFor === key) {
      return;
    }
    // A draft has no owner by definition; everything else needs one to key the read on.
    if (!this.draft && !this.ownerId) {
      return;
    }
    this.loadedFor = key;

    this.indexLoadSettled = false;
    this.libraryIndexes = [];
    this.libraryIndexNames = [];
    this.libraryCheckState = "pending";
    this.libraryCheckDetail = "";

    if (this.draft) {
      // No scope rows to read, and no library gap to report - a draft is seeded from
      // the library, so it cannot be missing anything the library defines.
      this.indexLoadSettled = true;
      this.loadDraft();
      return;
    }

    Promise.all([this.loadTarget(), this.loadIndexes(), this.loadBehaviour()])
      .then(([moduleTarget, moduleIndexes, behaviourFinetuning]) => {
        this.configPresence.emit(hasModuleConfig({ moduleTarget, moduleIndexes, behaviourFinetuning }));
      });
    this.loadLibraryIndexes();
  }

  // ---------------------------------------------------------------------------
  // "Configured in the library but not here" check
  // ---------------------------------------------------------------------------

  get ownerLabel(): string {
    const name = String(this.ownerName ?? "").trim();
    if (name) {
      return name;
    }
    return this.scope === "site" ? "this site" : "this sub-category";
  }

  /** Library indexes for this module that have no counterpart configured here. */
  get missingLibraryIndexes(): string[] {
    // Until the local list has landed, everything would look missing.
    if (!this.indexLoadSettled || !this.libraryIndexNames.length) {
      return [];
    }
    const configured = new Set(
      this.indexRows
        .map(row => String(row.index_name ?? "").trim().toLowerCase())
        .filter(Boolean)
    );
    return this.libraryIndexNames.filter(name => !configured.has(name.toLowerCase()));
  }

  /**
   * Copies the library's missing indexes into this owner. Matching is by name, so
   * copying rather than retyping is also what keeps the two in step afterwards.
   * The PureBI formula is deliberately not copied — the library stores parameter
   * names, this scope stores this module's codes, so the user runs Create Formula.
   */
  addMissingFromLibrary(): void {
    const missing = new Set(this.missingLibraryIndexes.map(name => name.toLowerCase()));
    if (!missing.size) {
      return;
    }

    let nextLocalId = Math.min(
      0,
      ...this.indexRows.map(row => Number(row.index_id) || 0)
    );

    this.libraryIndexes
      .filter(record => missing.has(String(record.index_name).trim().toLowerCase()))
      .forEach(record => {
        nextLocalId--;
        this.indexRows.push({
          index_id: nextLocalId,
          index_code: "",
          index_name: String(record.index_name).trim(),
          description: record.description ?? "",
          formula: record.formula ?? "",
          purebi_formula: "",
          aspects: this.parseAspects(record.aspect),
          weightage: this.toNullableNumber(record.weightage) ?? 0,
          selected: true,
          // Never taken from the library: copying must not move this unit's mark.
          health_index: false,
        });
      });

    this.queueSave("index");
    this.messageService.add({
      severity: "success",
      summary: "Added from library",
      detail: `${missing.size} index(es) copied. Run Create Formula to generate their `
        + `PureBI form from this module's parameter codes.`,
      life: 8000,
    });
  }

  private loadLibraryIndexes(): void {
    if (!this.moduleId) {
      this.libraryCheckState = "unavailable";
      this.libraryCheckDetail = "No module id was passed to this dialog.";
      return;
    }

    this.service.getLibraryModuleIndexes(this.moduleId).then((response: any) => {
      if (response && response.status === 1) {
        // Whole records, not just names, so the missing ones can be copied across.
        this.libraryIndexes = (response.indexes ?? [])
          .filter((record: any) => String(record?.index_name ?? "").trim());
        this.libraryIndexNames = this.libraryIndexes
          .map((record: any) => String(record.index_name).trim());
        this.libraryCheckState = "ok";
        this.libraryCheckDetail = `${this.libraryIndexNames.length} index(es) defined in the library `
          + `for module ${this.moduleId}.`;
      } else {
        this.libraryCheckState = "unavailable";
        this.libraryCheckDetail = response?.msg
          || `The library returned no result for module ${this.moduleId}.`;
      }
    }).catch((error: any) => {
      // Advisory only - a failure must not block configuring the module. Surfaced in
      // the Indexes tab rather than swallowed, so a missing/forbidden endpoint is
      // visible instead of looking like "nothing to report".
      this.libraryCheckState = "unavailable";
      this.libraryCheckDetail = `Could not read the library's indexes for module `
        + `${this.moduleId} (${error?.status ?? "network error"}).`;
    });
  }


  // Each load resolves to its response when it applied one, or null — never rejects,
  // so loadAll can wait on all three to report config presence.

  private loadTarget(): Promise<any> {
    this.saveQueues["target"].loading = true;
    return this.service.getModuleTarget(this.scope, this.moduleConfigRequest).then((response: any) => {
      this.saveQueues["target"].loading = false;
      if (!response || response.status !== 1) {
        return null;
      }
      this.applyTargetResponse(response);
      return response;
    }).catch(() => {
      this.saveQueues["target"].loading = false;
      this.loadedFor = "";
      return null;
    });
  }

  private loadIndexes(): Promise<any> {
    this.saveQueues["index"].loading = true;
    return this.service.getModuleIndexes(this.scope, this.moduleConfigRequest).then((response: any) => {
      this.saveQueues["index"].loading = false;
      if (!response || response.status !== 1) {
        // A failed read means "nothing configured here", which is exactly the case the
        // library-gap warning exists for - so it must still run.
        this.indexLoadSettled = true;
        return null;
      }
      this.applyIndexResponse(response);
      // Either load can land last; both call this, the flag makes it fire once.
      this.indexLoadSettled = true;
      return response;
    }).catch(() => {
      this.saveQueues["index"].loading = false;
      this.indexLoadSettled = true;
      return null;
    });
  }

  private loadBehaviour(): Promise<any> {
    this.saveQueues["behaviour"].loading = true;
    return this.service.getBehaviourFinetuning(this.scope, this.moduleConfigRequest).then((response: any) => {
      this.saveQueues["behaviour"].loading = false;
      if (!response || response.status !== 1) {
        return null;
      }
      this.applyBehaviourResponse(response);
      return response;
    }).catch(() => {
      this.saveQueues["behaviour"].loading = false;
      return null;
    });
  }

  // ---------------------------------------------------------------------------
  // Response -> view state
  //
  // Three sources feed these: the scope endpoints, the module library (draft seeding)
  // and a restored draft. All three carry the same field names, because the draft is
  // literally the save-request body, so one mapper each serves all three.
  // ---------------------------------------------------------------------------

  private applyTargetResponse(response: any): void {
    this.mergeAspectOptions(response.aspect_options);
    this.activeAspects = (response.module_aspects ?? []).map((a: any) => String(a));

    const stored = new Map<string, any>(
      (response.kpis ?? []).map((kpi: any) => [String(kpi.parameter_id), kpi])
    );
    this.targetRows.forEach(row => {
      const record = stored.get(String(row.parameter_id));
      if (!record) return;
      row.target_min = record.target_min ?? null;
      row.target_max = record.target_max ?? null;
      row.design_value = record.design_value ?? null;
      row.price = record.price ?? null;
      row.aspects = this.parseAspects(record.aspect);
      row.analysis.anomalies = !!record.analysis_anomalies;
      row.analysis.trends = !!record.analysis_trends;
      row.selected = !!record.is_selected;
      row.removed = !!record.is_removed;
    });
    this.targetRows = this.targetRows.filter(row => !row.removed);
    this.syncBehaviour();
  }

  private applyIndexResponse(response: any): void {
    this.indexRows = (response.indexes ?? []).map((record: any) => ({
      // A library-seeded or restored index is not a row in this scope's table yet, so
      // its id must not be carried across - it belongs to a different table.
      index_id: this.draft ? null : record.index_id,
      // Assigned by the backend against a row in THIS scope's table, so a library-seeded
      // or restored draft row has not earned one yet - same reasoning as index_id above.
      index_code: this.draft ? "" : (record.index_code ?? ""),
      index_name: record.index_name ?? "",
      description: record.description ?? "",
      formula: record.formula ?? "",
      purebi_formula: record.purebi_formula ?? "",
      aspects: this.parseAspects(record.aspect),
      weightage: this.toNullableNumber(record.weightage) ?? 0,
      selected: !!record.is_selected,
      health_index: !!record.is_health_index,
    }));

    // At most one. The server refuses a save marking two, so a second mark read
    // back would fail every later save of this tab; the first one stands.
    const health = this.indexRows.find(row => row.health_index);
    this.indexRows.forEach(row => row.health_index = row === health);
  }

  private applyBehaviourResponse(response: any): void {
    const store: { [aspect: string]: any } = {};
    (response.behaviours ?? []).forEach((record: any) => {
      const aspect = String(record?.aspect ?? "").trim();
      if (!aspect) return;
      store[aspect] = {
        behaviour: record.behaviour ?? "",
        finetuning: record.finetuning ?? "",
      };
    });
    this.behaviourStore = store;
    this.syncBehaviour();
  }

  // ---------------------------------------------------------------------------
  // Draft mode
  // ---------------------------------------------------------------------------

  /**
   * Nothing to read - the sub-category does not exist yet. Restore what the user
   * entered last time the dialog was open, or, first time through, seed from the
   * module library so they start from the type's definition instead of blank grids.
   */
  private loadDraft(): void {
    if (this.hasDraftContent(this.draftValue)) {
      this.applyTargetResponse(this.draftValue.moduleTarget ?? {});
      this.applyIndexResponse(this.draftValue.moduleIndexes ?? {});
      this.applyBehaviourResponse(this.draftValue.behaviourFinetuning ?? {});
      this.configPresence.emit(hasModuleConfig(this.draftValue));
      return;
    }
    this.seedFromLibrary();
  }

  private hasDraftContent(value: ModuleConfigDraft): boolean {
    if (!value) {
      return false;
    }
    return !!(
      value.moduleTarget?.kpis?.length ||
      value.moduleTarget?.module_aspects?.length ||
      value.moduleIndexes?.indexes?.length ||
      value.behaviourFinetuning?.behaviours?.length
    );
  }

  /**
   * Seed the three tabs from the System-Admin library definition of this module type.
   * Advisory: a library with nothing defined, or an endpoint that fails, leaves the tab
   * empty and the user fills it in by hand. It must never block configuring the module.
   */
  private seedFromLibrary(): void {
    if (!this.moduleId) {
      return;
    }

    const settle = (kind: string, apply: (response: any) => void) => (response: any) => {
      this.saveQueues[kind].loading = false;
      if (response && response.status === 1) {
        apply(response);
      }
    };

    this.saveQueues["target"].loading = true;
    this.saveQueues["index"].loading = true;
    this.saveQueues["behaviour"].loading = true;

    Promise.all([
      this.service.getLibraryModuleTarget(this.moduleId)
        .then(settle("target", response => this.applyTargetResponse(response)))
        .catch(() => { this.saveQueues["target"].loading = false; }),
      this.service.getLibraryModuleIndexes(this.moduleId)
        .then(settle("index", response => this.applyIndexResponse(response)))
        .catch(() => { this.saveQueues["index"].loading = false; }),
      this.service.getLibraryModuleBehaviour(this.moduleId)
        .then(settle("behaviour", response => this.applyBehaviourResponse(response)))
        .catch(() => { this.saveQueues["behaviour"].loading = false; }),
    ]).then(() => {
      // Emit even if the user never touches a tab: a seeded module must still travel
      // with the create payload, or seeding would be cosmetic. Seeded config is written
      // with the sub-category, so it counts as present.
      this.configPresence.emit(hasModuleConfig(this.emitDraft()));
    });
  }

  private emitDraft(): ModuleConfigDraft {
    const draft: ModuleConfigDraft = {
      moduleTarget: this.stripScope(this.buildTargetRequest()),
      moduleIndexes: this.stripScope(this.buildIndexRequest()),
      behaviourFinetuning: this.stripScope(this.buildBehaviourRequest()),
    };
    this.draftChange.emit(draft);
    return draft;
  }

  /**
   * Drop the scope block from a draft. The owner is unknown while drafting, and the
   * module is identified by the `moduleConfigs` entry the draft is attached to, so
   * sending `subcategory_id: null` would be noise the backend has to ignore.
   */
  private stripScope(request: any): any {
    const { site_id, subcategory_id, category_id, module_code, ...rest } = request ?? {};
    return rest;
  }

  private mergeAspectOptions(options: any[]): void {
    (options ?? []).forEach((aspect: any) => {
      const name = String(aspect ?? "").trim();
      if (name && !this.aspectOptions.some(o => o.toLowerCase() === name.toLowerCase())) {
        this.aspectOptions.push(name);
      }
    });
  }

  private buildTargetRequest(): any {
    return {
      ...this.moduleConfigRequest,
      module_aspects: [...this.activeAspects],
      aspect_options: [...this.aspectOptions],
      kpis: this.targetRows.map(row => ({
        parameter_id: row.parameter_id,
        target_min: this.toNullableNumber(row.target_min),
        target_max: this.toNullableNumber(row.target_max),
        design_value: this.toNullableNumber(row.design_value),
        price: this.toNullableNumber(row.price),
        // Multi-select; stored comma-joined in the same column the single value used.
        aspect: this.joinAspects(row.aspects),
        analysis_anomalies: !!row.analysis.anomalies,
        analysis_trends: !!row.analysis.trends,
        is_selected: !!row.selected,
        is_removed: !!row.removed,
      })),
    };
  }

  private buildIndexRequest(): any {
    return {
      ...this.moduleConfigRequest,
      indexes: this.indexRows.map((row, position) => ({
        index_id: row.index_id > 0 ? row.index_id : null,
        // Echoed back so the backend keeps a code it already assigned; null means "this
        // row has none yet, assign one". It is never generated client-side - the code
        // must be unique within the unit, and only the server can guarantee that.
        index_code: row.index_code || null,
        index_name: row.index_name,
        description: row.description || null,
        formula: row.formula || null,
        purebi_formula: row.purebi_formula || null,
        aspect: this.joinAspects(row.aspects),
        weightage: this.toNullableNumber(row.weightage) ?? 0,
        is_selected: !!row.selected,
        is_health_index: !!row.health_index,
        sort_order: position,
      })),
    };
  }

  private buildBehaviourRequest(): any {
    return {
      ...this.moduleConfigRequest,
      behaviours: Object.keys(this.behaviourStore)
        .filter(aspect =>
          String(this.behaviourStore[aspect].behaviour ?? "").trim() ||
          String(this.behaviourStore[aspect].finetuning ?? "").trim()
        )
        .map(aspect => ({
          aspect,
          behaviour: this.behaviourStore[aspect].behaviour || null,
          finetuning: this.behaviourStore[aspect].finetuning || null,
        })),
    };
  }

  private toNullableNumber(value: any): number {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  // ---------------------------------------------------------------------------
  // Aspects
  // ---------------------------------------------------------------------------

  isAspectSelected(aspect: string): boolean {
    return this.activeAspects.includes(aspect);
  }

  toggleAspect(aspect: string): void {
    const index = this.activeAspects.indexOf(aspect);
    if (index >= 0) {
      this.activeAspects.splice(index, 1);
    } else {
      this.activeAspects.push(aspect);
    }
    this.syncBehaviour();
    this.queueSave("target");
  }

  toggleAspectDropdown(): void {
    this.aspectDropdownOpen = !this.aspectDropdownOpen;
    if (!this.aspectDropdownOpen) {
      this.newAspectName = "";
    }
  }

  closeAspectDropdown(): void {
    this.aspectDropdownOpen = false;
    this.newAspectName = "";
  }

  clearAspects(): void {
    this.activeAspects.length = 0;
    this.syncBehaviour();
    this.queueSave("target");
  }

  createAspect(): void {
    // A comma would split the name apart when a KPI's aspects are read back.
    const name = this.newAspectName.replace(/,/g, " ").replace(/\s+/g, " ").trim();
    if (!name) {
      return;
    }

    const existing = this.aspectOptions.find(a => a.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!this.isAspectSelected(existing)) {
        this.activeAspects.push(existing);
        this.syncBehaviour();
        this.queueSave("target");
      }
      this.newAspectName = "";
      return;
    }

    this.aspectOptions.push(name);
    this.activeAspects.push(name);
    this.newAspectName = "";
    this.syncBehaviour();
    this.queueSave("target");
  }

  isCustomAspect(aspect: string): boolean {
    return !DEFAULT_ASPECTS.some(b => b.toLowerCase() === String(aspect).toLowerCase());
  }

  deleteAspect(aspect: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.isCustomAspect(aspect)) {
      return;
    }

    this.confirm("Index", aspect).then(confirmed => {
      if (!confirmed) {
        return;
      }

      if (this.draft) {
        // `deleteAspect` removes the aspect from the shared vocabulary, across every
        // sub-category that uses it. An unsaved wizard has no business doing that, so
        // drop it from this draft only and leave the server's list alone.
        this.purgeAspect(aspect);
        this.queueSave("target");
        return;
      }

      this.service.deleteAspect(this.scope, { ...this.scopeRequest, aspect_name: aspect }).then((response: any) => {
        if (response && response.status === 1) {
          this.messageService.add({
            severity: "success",
            summary: "Delete Aspect",
            detail: response.msg || "Aspect deleted",
          });
          this.purgeAspect(aspect);
          return;
        }
        this.messageService.add({
          severity: "error",
          summary: "Delete Aspect",
          detail: response?.msg || "Unable to delete index",
        });
      }).catch(() => {
        this.messageService.add({
          severity: "error",
          summary: "Delete Aspect",
          detail: "Unable to delete index",
        });
      });
    });
  }

  /** Mirrors the server-side cascade so the UI is consistent without a reload. */
  private purgeAspect(aspect: string): void {
    const name = String(aspect).toLowerCase();

    this.aspectOptions = this.aspectOptions.filter(o => o.toLowerCase() !== name);
    for (let i = this.activeAspects.length - 1; i >= 0; i--) {
      if (this.activeAspects[i].toLowerCase() === name) {
        this.activeAspects.splice(i, 1);
      }
    }
    this.targetRows.forEach(row => {
      row.aspects = (row.aspects ?? []).filter((a: string) => a.toLowerCase() !== name);
    });
    this.indexRows.forEach(row => {
      row.aspects = (row.aspects ?? []).filter((a: string) => a.toLowerCase() !== name);
    });
    Object.keys(this.behaviourStore).forEach(key => {
      if (key.toLowerCase() === name) delete this.behaviourStore[key];
    });

    this.syncBehaviour();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.aspectDropdownOpen) {
      return;
    }
    const host = this.aspectDropdown?.nativeElement;
    if (host && !host.contains(event.target as Node)) {
      this.closeAspectDropdown();
    }
  }

  @HostListener("document:keydown.escape")
  onEscapeKey(): void {
    this.closeAspectDropdown();
    if (this.addParamVisible) {
      this.closeAddParameters();
    }
  }

  // ---------------------------------------------------------------------------
  // Module Target
  // ---------------------------------------------------------------------------

  private syncTargetRows(): void {
    const existing = new Map<string, any>(
      this.targetRows.map(row => [String(row.parameter_id), row])
    );
    const seen = new Set<string>();
    const rows: any[] = [];

    (this.parameters ?? []).forEach((parameter: any) => {
      const id = String(parameter?.parameter_id ?? "");
      if (!id || seen.has(id)) return;
      seen.add(id);

      const row = existing.get(id) ?? {
        parameter_id: id,
        target_min: null,
        target_max: null,
        design_value: null,
        price: null,
        aspects: [],
        analysis: { anomalies: false, trends: false },
        selected: false,
        removed: false,
      };
      row.parameter_name = parameter.name ?? parameter.parameters ?? "";
      row.parameter_unit = parameter.unit ?? "";
      // Display/formula code. Falls back to the id so an uncoded parameter still
      // resolves in a formula rather than silently dropping out.
      row.parameter_code = String(parameter.code ?? "").trim() || id;
      rows.push(row);
    });

    this.targetRows = rows.filter(row => !row.removed);
  }

  get isTargetSearchActive(): boolean {
    return !!this.targetSearchTerm.trim();
  }

  private matchesTargetTerm(row: any, term: string): boolean {
    return (
      String(row.parameter_name ?? "").toLowerCase().includes(term) ||
      String(row.parameter_unit ?? "").toLowerCase().includes(term) ||
      String(row.parameter_code ?? "").toLowerCase().includes(term) ||
      String(row.parameter_id ?? "").toLowerCase().includes(term) ||
      (row.aspects ?? []).some((a: string) => a.toLowerCase().includes(term))
    );
  }

  /**
   * The KPI list: the parameters actually added to this module's target.
   *
   * `targetRows` still holds every parameter configured on the module. An unselected
   * row keeps whatever min/max/design/price was entered before it was taken off the
   * list, so re-adding it through the picker brings those values back with it. Those
   * rows are the picker's pool and are never rendered in the grid.
   */
  get selectedTargetRows(): any[] {
    return this.targetRows.filter(row => row.selected);
  }

  get filteredTargetRows(): any[] {
    const term = this.targetSearchTerm.trim().toLowerCase();
    const rows = this.selectedTargetRows;
    return term ? rows.filter(row => this.matchesTargetTerm(row, term)) : rows;
  }

  get selectedTargetCount(): number {
    return this.selectedTargetRows.length;
  }

  get allTargetsSelected(): boolean {
    const rows = this.filteredTargetRows;
    return !!rows.length && rows.every(row => row.selected);
  }

  get someTargetsSelected(): boolean {
    const rows = this.filteredTargetRows;
    const selected = rows.filter(row => row.selected).length;
    return selected > 0 && selected < rows.length;
  }

  get targetSelectionSummary(): string {
    const total = this.targetRows.length;
    if (!total) {
      return "No parameters available for analysis.";
    }
    const selected = this.selectedTargetCount;
    return selected
      ? `${selected} of ${total} configured parameters added for analysis.`
      : `No parameters added yet. ${total} available to add.`;
  }

  get targetFilterSummary(): string {
    if (!this.isTargetSearchActive) {
      return "";
    }
    const matches = this.filteredTargetRows.length;
    return matches ? `Showing ${matches} of ${this.selectedTargetCount}.` : "No matches.";
  }

  toggleTargetSelection(row: any): void {
    row.selected = !row.selected;
    this.reconcileAspects(row.aspects ?? []);
    this.queueSave("target");
  }

  toggleAllTargets(): void {
    const rows = this.filteredTargetRows;
    const select = !this.allTargetsSelected;
    rows.forEach(row => row.selected = select);
    this.reconcileAspects(rows.flatMap(row => row.aspects ?? []));
    this.queueSave("target");
  }

  toggleAnalysisMode(row: any, modeId: string): void {
    row.analysis[modeId] = !row.analysis[modeId];
    this.queueSave("target");
  }

  removeTargetRow(row: any): void {
    row.removed = true;
    this.targetRows = this.targetRows.filter(r => !r.removed);
    this.reconcileAspects(row.aspects ?? []);
    this.queueSave("target");
  }

  clearTargetSearch(): void {
    this.targetSearchTerm = "";
  }

  onTargetChanged(): void {
    this.queueSave("target");
  }

  onTargetAspectsChanged(row: any, aspects: string[]): void {
    const previous: string[] = row.aspects ?? [];
    row.aspects = [...(aspects ?? [])];
    this.reconcileAspects([...previous, ...row.aspects]);
    this.onTargetChanged();
  }

  /** A KPI's or index's aspects travel as one comma-joined string in the `aspect` field. */
  private parseAspects(value: any): string[] {
    const list = Array.isArray(value) ? value : String(value ?? "").split(",");
    const seen = new Set<string>();
    return list
      .map(a => String(a ?? "").trim())
      .filter(a => a && !seen.has(a.toLowerCase()) && !!seen.add(a.toLowerCase()));
  }

  private joinAspects(aspects: string[]): string | null {
    return (aspects ?? []).length ? aspects.join(", ") : null;
  }

  /**
   * The Aspect list follows the rows: an aspect a KPI or index row uses is selected,
   * and one no row uses any more is unselected. Only the aspects a change touched are
   * looked at, so one picked by hand in the Aspect list with no row behind it stays.
   * module_aspects is saved with the target, whichever tab the change came from.
   */
  private reconcileAspects(aspects: string[]): void {
    let changed = false;
    aspects.map(a => String(a ?? "").trim()).filter(Boolean).forEach(name => {
      const key = name.toLowerCase();
      const used = this.isAspectInUse(key);
      const index = this.activeAspects.findIndex(a => a.toLowerCase() === key);
      if (used && index < 0) {
        this.activeAspects.push(name);
        changed = true;
      } else if (!used && index >= 0) {
        this.activeAspects.splice(index, 1);
        changed = true;
      }
    });
    if (changed) {
      this.syncBehaviour();
      this.queueSave("target");
    }
  }

  /** Unselected target rows are the picker's pool, not KPIs, so only the list counts. */
  private isAspectInUse(key: string): boolean {
    return [...this.selectedTargetRows, ...this.indexRows]
      .some(row => (row.aspects ?? []).some((a: string) => a.toLowerCase() === key));
  }

  trackTargetRow = (_index: number, row: any) => row.parameter_id;

  // ---------------------------------------------------------------------------
  // Add Parameter picker
  //
  // Nothing is fetched here: the pool is this module's own Configure Parameter rows,
  // already in `targetRows`. A target KPI is keyed on `parameter_id`, so a parameter
  // that is not configured on the module cannot be one - adding a brand-new parameter
  // stays on tab 1, which is also where it gets its code, type and weight.
  // ---------------------------------------------------------------------------

  /** Configured parameters not on the KPI list yet - what the picker offers. */
  get availableTargetRows(): any[] {
    return this.targetRows.filter(row => !row.selected);
  }

  get filteredAvailableRows(): any[] {
    const term = this.addParamSearchTerm.trim().toLowerCase();
    const rows = this.availableTargetRows;
    return term ? rows.filter(row => this.matchesTargetTerm(row, term)) : rows;
  }

  get isAddParamSearchActive(): boolean {
    return !!this.addParamSearchTerm.trim();
  }

  get addParamSelectionCount(): number {
    return this.addParamChosen.size;
  }

  get allAddParamsSelected(): boolean {
    const rows = this.filteredAvailableRows;
    return !!rows.length && rows.every(row => this.isAddParamSelected(row));
  }

  get someAddParamsSelected(): boolean {
    const rows = this.filteredAvailableRows;
    const chosen = rows.filter(row => this.isAddParamSelected(row)).length;
    return chosen > 0 && chosen < rows.length;
  }

  isAddParamSelected(row: any): boolean {
    return this.addParamChosen.has(String(row.parameter_id));
  }

  toggleAddParam(row: any): void {
    const id = String(row.parameter_id);
    if (this.addParamChosen.has(id)) {
      this.addParamChosen.delete(id);
      return;
    }
    this.addParamChosen.add(id);
  }

  /** Ticks or clears every row the picker's own search currently shows. */
  toggleAllAddParams(): void {
    const rows = this.filteredAvailableRows;
    const select = !this.allAddParamsSelected;
    rows.forEach(row => {
      const id = String(row.parameter_id);
      if (select) {
        this.addParamChosen.add(id);
      } else {
        this.addParamChosen.delete(id);
      }
    });
  }

  clearAddParamSearch(): void {
    this.addParamSearchTerm = "";
  }

  openAddParameters(): void {
    this.addParamChosen.clear();
    this.addParamSearchTerm = "";
    this.addParamVisible = true;
  }

  closeAddParameters(): void {
    this.addParamVisible = false;
    this.addParamChosen.clear();
    this.addParamSearchTerm = "";
  }

  confirmAddParameters(): void {
    if (!this.addParamChosen.size) {
      return;
    }

    const added: any[] = [];
    this.targetRows.forEach(row => {
      if (row.selected || !this.addParamChosen.has(String(row.parameter_id))) {
        return;
      }
      row.selected = true;
      added.push(row);
    });

    this.closeAddParameters();
    if (!added.length) {
      return;
    }

    // A re-added row brings back the aspect it was kept with.
    this.reconcileAspects(added.flatMap(row => row.aspects ?? []));

    // A live search would hide the rows just added, which reads as the Add having
    // done nothing at all.
    this.targetSearchTerm = "";
    this.queueSave("target");
    this.messageService.add({
      severity: "success",
      summary: "Module Target",
      detail: `${added.length} parameter(s) added to the KPI list.`,
    });
  }

  // ---------------------------------------------------------------------------
  // Module Indexes
  // ---------------------------------------------------------------------------

  get selectedIndexRows(): any[] {
    return this.indexRows.filter(row => row.selected);
  }

  get totalIndexWeightage(): number {
    const total = this.selectedIndexRows.reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);
    return Math.round(total * 100) / 100;
  }

  get isIndexWeightageValid(): boolean {
    return !this.selectedIndexRows.length || this.totalIndexWeightage === 100;
  }

  get allIndexesSelected(): boolean {
    return !!this.indexRows.length && this.indexRows.every(row => row.selected);
  }

  get someIndexesSelected(): boolean {
    const selected = this.selectedIndexRows.length;
    return selected > 0 && selected < this.indexRows.length;
  }

  toggleIndexSelection(row: any): void {
    row.selected = !row.selected;
    this.queueSave("index");
  }

  toggleAllIndexes(): void {
    const select = !this.allIndexesSelected;
    this.indexRows.forEach(row => row.selected = select);
    this.queueSave("index");
  }

  /**
   * One Health Index per module: switching a row on switches every other row off.
   * Switching the current one off leaves the module with none.
   */
  toggleHealthIndex(row: any): void {
    const on = !row.health_index;
    this.indexRows.forEach(r => r.health_index = false);
    row.health_index = on;
    this.queueSave("index");
  }

  distributeEvenly(): void {
    const rows = this.selectedIndexRows;
    if (!rows.length) {
      return;
    }
    const base = Math.floor(100 / rows.length);
    let remainder = 100 - (base * rows.length);
    rows.forEach(row => {
      row.weightage = base + (remainder > 0 ? 1 : 0);
      remainder--;
    });
    this.queueSave("index");
  }

  addIndexRow(): void {
    const nextLocalId = Math.min(
      0,
      ...this.indexRows.map(row => Number(row.index_id) || 0)
    ) - 1;

    this.indexRows.push({
      index_id: nextLocalId,
      // Assigned by the backend on the first save; blank until then.
      index_code: "",
      index_name: `Index ${this.indexRows.length + 1}`,
      description: "",
      formula: "",
      purebi_formula: "",
      aspects: [],
      weightage: 0,
      selected: true,
      health_index: false,
    });
    this.queueSave("index");
  }

  removeIndexRow(row: any): void {
    const index = this.indexRows.indexOf(row);
    if (index >= 0) {
      this.indexRows.splice(index, 1);
      this.reconcileAspects(row.aspects ?? []);
      this.queueSave("index");
    }
  }

  onIndexChanged(row?: any): void {
    // None of these notes may outlive the formula they described.
    if (row) {
      row.unresolved = null;
      row.borrowed = null;
      row.stale = false;
    }
    this.queueSave("index");
  }

  onIndexAspectsChanged(row: any, aspects: string[]): void {
    const previous: string[] = row.aspects ?? [];
    row.aspects = [...(aspects ?? [])];
    this.reconcileAspects([...previous, ...row.aspects]);
    this.onIndexChanged();
  }

  /**
   * Hand-editing the PureBI column takes ownership of it, so the "compiled from an
   * earlier version" note stops applying. The missing-parameter note describes the
   * readable formula, not this field, so it stays.
   */
  onPureBiChanged(row: any): void {
    if (row) {
      row.stale = false;
    }
    this.queueSave("index");
  }

  trackIndexRow = (_index: number, row: any) => row.index_id;

  /**
   * Compiles each index's readable formula into its PureBI form: drops the
   * "<Index> =" label and swaps parameter names for their parameter ids.
   */
  async createFormula(): Promise<void> {
    const rows = this.indexRows.filter(row => String(row.formula ?? "").trim());
    if (!rows.length) {
      this.messageService.add({
        severity: "warn",
        summary: "Create Formula",
        detail: "Write a formula on at least one index first.",
      });
      return;
    }

    this.creatingFormula = true;
    try {
      await this.ensureSiblingIndexes();
    } finally {
      this.creatingFormula = false;
    }

    const dictionary = this.formulaDictionary();
    if (!dictionary.length) {
      this.messageService.add({
        severity: "warn",
        summary: "Create Formula",
        detail: "There are no parameters or indexes anywhere in this plant to resolve "
          + "names against.",
      });
      return;
    }

    const pendingIndexes = this.pendingIndexNames();
    const unresolved = new Set<string>();
    const awaitingCode = new Set<string>();
    const borrowed = new Set<string>();
    let clean = 0;

    rows.forEach(row => {
      const compiled = this.toPureBiFormula(row.formula, dictionary);
      // Kept on the row so the notes stay beside the formula they describe, rather
      // than vanishing with the toast.
      row.unresolved = compiled.unresolved;

      if (compiled.unresolved.length) {
        compiled.unresolved.forEach(term =>
          (pendingIndexes.has(term.toLowerCase()) ? awaitingCode : unresolved).add(term));
        // A partial compile is not written. Half of it would be codes and half the
        // literal names that could not be resolved, which is not a formula in either
        // language - it cannot be evaluated, and it reads as though it worked. The
        // row is left exactly as it was and the warning below says why.
        row.borrowed = null;
        // Whatever is already in the column was compiled from an EARLIER version of
        // this formula, so it no longer describes the text beside it. Said plainly on
        // the row, because silently keeping a stale formula is the one way this can
        // still mislead.
        row.stale = !!String(row.purebi_formula ?? "").trim();
        return;
      }

      row.purebi_formula = compiled.expression;
      row.borrowed = compiled.borrowed;
      row.stale = false;
      compiled.borrowed.forEach(term => borrowed.add(term));
      clean++;
    });

    // Only when something was actually written. With every row skipped there is no
    // change to persist - the notes above are view state, not part of the request.
    if (clean) {
      this.queueSave("index");
    }

    if (unresolved.size || awaitingCode.size) {
      const skipped = rows.length - clean;
      const reasons: string[] = [];
      if (unresolved.size) {
        reasons.push(`Not found anywhere in this plant: `
          + `${Array.from(unresolved).join(", ")} - check the spelling against the `
          + `Parameter column on the Module Target tab.`);
      }
      if (awaitingCode.size) {
        reasons.push(`Waiting for an index code: ${Array.from(awaitingCode).join(", ")} `
          + `- save the index once and its code is assigned, then run this again.`);
      }
      this.messageService.add({
        severity: "warn",
        summary: unresolved.size ? "Missing parameters" : "Index not saved yet",
        detail: `${clean} of ${rows.length} formula(s) fully resolved. `
          + `${skipped} left unchanged - a formula is only written once every name in `
          + `it resolves. ${reasons.join(" ")}`,
        life: 12000,
      });
      return;
    }

    if (borrowed.size) {
      this.messageService.add({
        severity: "success",
        summary: "Create Formula",
        detail: `Generated ${rows.length} PureBI formula(s). `
          + `${borrowed.size} operand(s) belong to another unit and were written `
          + `unit-qualified: ${Array.from(borrowed).join(", ")}.`,
        life: 10000,
      });
      return;
    }

    this.messageService.add({
      severity: "success",
      summary: "Create Formula",
      detail: `Generated ${rows.length} PureBI formula(s).`,
    });
  }

  /**
   * Every name a formula on this module may reference, and the text each compiles to.
   *
   * Built in precedence order, because the dedupe below is first-wins and names are NOT
   * unique across a plant — two units can both call a parameter "MLSS" while holding
   * different codes. Nearest scope has to win, or a local formula would silently bind to
   * another unit's sensor:
   *
   *   1. this module's Module Target rows   -> its code   (`1A63`)
   *   2. its Configure Parameter rows       -> its code   (`1A63`)
   *   3. another unit's parameters          -> its code   (`1G1`)
   *   4. indexes, own then other units      -> its code   (`1GIN1`)
   *
   * Everything compiles to its own code wherever it lives: codes are generated as the
   * unit's grid code plus a sequence (`1G` + `1`), so the owning unit is already inside
   * the code and a second copy of it in front would be noise.
   *
   * An index the backend has not yet assigned a code to contributes nothing here — it
   * has no operand to substitute. `pendingIndexNames` recognises those separately so the
   * user is told to save the index rather than to check their spelling.
   *
   * Rule 1 vs 2 is what keeps every formula stored before this change compiling to the
   * exact same string. Rule 3 exists because an index has no code of its own — the
   * `*_moduleIndexes` table has no such column and `index_id` is null until first save —
   * so an index reference is passed through for the backend to resolve, and its only
   * effect here is that it stops being reported as a missing parameter.
   */
  private formulaDictionary(): DictionaryEntry[] {
    const seen = new Set<string>();
    const list: DictionaryEntry[] = [];

    const add = (name: string, code: string, owner: string | null, kind: "parameter" | "index") => {
      const cleanName = String(name ?? "").trim();
      const cleanCode = String(code ?? "").trim();
      if (!cleanName || !cleanCode || seen.has(cleanName.toLowerCase())) return;
      seen.add(cleanName.toLowerCase());
      list.push({ name: cleanName, code: cleanCode, owner, kind });
    };

    this.targetRows.forEach(row =>
      add(row.parameter_name, row.parameter_code ?? row.parameter_id, null, "parameter"));

    // Same module, so still a bare code and still ahead of any other unit. Adds only
    // what the Target grid dropped - anything on both lists was taken above.
    this.configuredParameters.forEach((parameter: any) =>
      add(parameter?.name, parameter?.code, null, "parameter"));

    this.siblingModules.forEach(sibling => {
      (sibling.parameters ?? []).forEach(parameter =>
        add(
          parameter?.name,
          parameter?.code,
          sibling.label || String(sibling.moduleCode ?? ""),
          "parameter"
        ));
    });

    this.indexRows.forEach(row => add(row.index_name, row.index_code, null, "index"));
    this.siblingModules.forEach(sibling =>
      (sibling.indexes ?? []).forEach(index =>
        add(index?.name, index?.code, sibling.label || String(sibling.moduleCode ?? ""), "index")));

    // Longest first, so "Permeate TDS" beats a parameter merely called "TDS".
    return list.sort((a, b) => b.name.length - a.name.length);
  }

  /**
   * Names of indexes that exist but have no code yet, lowercased.
   *
   * `index_code` is assigned by the backend on insert, so an index added in this session
   * and not yet saved has none. It cannot be an operand, but it is not a typo either —
   * telling the user to check the spelling of a name they can see on screen would send
   * them looking for a problem that is not there.
   */
  private pendingIndexNames(): Set<string> {
    const pending = new Set<string>();
    const consider = (name: any, code: any) => {
      const cleanName = String(name ?? "").trim();
      if (cleanName && !String(code ?? "").trim()) {
        pending.add(cleanName.toLowerCase());
      }
    };
    this.indexRows.forEach(row => consider(row.index_name, row.index_code));
    this.siblingModules.forEach(sibling =>
      (sibling.indexes ?? []).forEach(index => consider(index?.name, index?.code)));
    return pending;
  }

  /**
   * Indexes belonging to the other units, which - unlike their parameters - the plant
   * screen does not hold: only a draft carries them in memory, a saved owner keeps them
   * in the scope table. Fetched once per dialog, on first Create Formula rather than on
   * open, so a user who never compiles a formula never pays for them.
   *
   * Advisory throughout. A unit that fails to answer simply contributes no names, which
   * costs a "missing parameter" warning on a formula that references one of its indexes
   * - never a wrong code, since index names compile to themselves either way.
   */
  private async ensureSiblingIndexes(): Promise<void> {
    if (this.siblingIndexesLoaded || this.draft || !this.ownerId) {
      return;
    }
    this.siblingIndexesLoaded = true;

    const pending = this.siblingModules.filter(
      sibling => !(sibling.indexes ?? []).length && String(sibling?.moduleCode ?? "").trim()
    );
    if (!pending.length) {
      return;
    }

    await Promise.all(pending.map(sibling =>
      this.service
        .getModuleIndexes(this.scope, {
          ...this.scopeRequest,
          module_id: sibling.moduleId,
          module_code: sibling.moduleCode,
        })
        .then((response: any) => {
          if (!response || response.status !== 1) return;
          sibling.indexes = (response.indexes ?? [])
            .map((record: any) => ({
              name: String(record?.index_name ?? "").trim(),
              code: String(record?.index_code ?? "").trim(),
            }))
            .filter((index: any) => index.name);
        })
        .catch(() => { /* advisory - see doc comment */ })
    ));
  }

  private toPureBiFormula(formula: any, dictionary: DictionaryEntry[]):
    { expression: string, unresolved: string[], borrowed: string[] } {
    let expression = this.stripFormulaLabel(String(formula ?? ""));
    const matched = new Set<number>();

    // Substitute to opaque placeholders first, so a code containing letters is not
    // then mistaken for an unresolved parameter name.
    dictionary.forEach((entry, index) => {
      const pattern = new RegExp(
        `(?<![A-Za-z0-9_])${this.escapeRegExp(entry.name)}(?![A-Za-z0-9_])`, "gi"
      );
      const before = expression;
      expression = expression.replace(pattern, `${PLACEHOLDER}${index}${PLACEHOLDER}`);
      if (expression !== before) {
        matched.add(index);
      }
    });

    // "x" is the multiplication sign a keyboard actually has, so people type it
    // constantly where the UI shows "×". Normalised only HERE, after pass 1: every
    // name that resolved is already a placeholder, so this can never eat a letter
    // belonging to a parameter. The boundary guards leave "Flux" and "Max" alone.
    expression = expression.replace(/(?<![A-Za-z0-9_])[xX](?![A-Za-z0-9_])/g, "×");

    const unresolved = this.findUnresolvedTerms(expression);

    dictionary.forEach((entry, index) => {
      expression = expression.split(`${PLACEHOLDER}${index}${PLACEHOLDER}`).join(entry.code);
    });

    // Only foreign PARAMETERS are called out: they are the ones whose emitted text
    // changed shape. A foreign index name compiles to itself, exactly as a local one
    // does, so flagging it would be noise.
    const borrowed = Array.from(matched)
      .map(index => dictionary[index])
      .filter(entry => entry.owner && entry.kind === "parameter")
      .map(entry => `${entry.name} (${entry.owner})`);

    return { expression: expression.trim(), unresolved, borrowed };
  }

  /** Drops a leading "Quality =" label but leaves comparisons such as ">=" untouched. */
  private stripFormulaLabel(formula: string): string {
    const match = formula.match(/^([^=<>!]+)=(?!=)(.*)$/);
    if (!match) {
      return formula.trim();
    }
    return /[+\-*/×÷()]/.test(match[1]) ? formula.trim() : match[2].trim();
  }

  /**
   * Whatever text is left once resolved parameters have become placeholders is a name
   * the formula referenced but this module does not have. Reported as whole runs —
   * parameter names contain spaces, `-` and `/` (e.g. "DAF-A I/L Flow"), so splitting
   * per word would report five useless fragments instead of one actionable name.
   */
  private findUnresolvedTerms(expression: string): string[] {
    const stripped = expression
      .split(new RegExp(`${PLACEHOLDER}\\d+${PLACEHOLDER}`, "g"))
      .join(" ");

    const runs = stripped.match(/[A-Za-z][A-Za-z0-9_.\-\/ ]*/g) ?? [];
    const terms: string[] = [];

    runs.forEach(run => {
      // A spaced operator can never occur inside a parameter name, so it is a safe
      // boundary — this drops the trailing "/ 98" from "DAF-A I/L Flow / 98".
      run.split(/\s+[+\-*/×÷]\s+/).forEach(part => {
        const term = part.trim();
        if (term && /[A-Za-z]/.test(term) && !FORMULA_FUNCTIONS.includes(term.toLowerCase())) {
          terms.push(term);
        }
      });
    });

    return Array.from(new Set(terms));
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // ---------------------------------------------------------------------------
  // Behaviour & Finetuning
  // ---------------------------------------------------------------------------

  setBehaviourAspect(aspect: string): void {
    this.behaviourAspect = aspect;
    this.syncBehaviour();
  }

  onBehaviourChanged(): void {
    this.queueSave("behaviour");
  }

  private syncBehaviour(): void {
    if (!this.behaviourAspect || !this.activeAspects.includes(this.behaviourAspect)) {
      this.behaviourAspect = this.activeAspects[0] ?? "";
    }
    if (!this.behaviourAspect) {
      this.behaviourRecord = null;
      return;
    }
    if (!this.behaviourStore[this.behaviourAspect]) {
      this.behaviourStore[this.behaviourAspect] = { behaviour: "", finetuning: "" };
    }
    this.behaviourRecord = this.behaviourStore[this.behaviourAspect];
  }

  // ---------------------------------------------------------------------------
  // Confirmation (self-contained; the host dialog owns no confirm UI)
  // ---------------------------------------------------------------------------

  private confirm(title: string, target: string): Promise<boolean> {
    this.confirmTitle = title;
    this.confirmTarget = target;
    this.confirmVisible = true;
    return new Promise<boolean>(resolve => {
      this.confirmResolve = resolve;
    });
  }

  resolveConfirm(result: boolean): void {
    this.confirmVisible = false;
    const resolve = this.confirmResolve;
    this.confirmResolve = null;
    resolve?.(result);
  }
}
